import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowRight, Check, MapPin, Clock, Sparkles, ChevronLeft } from "lucide-react";
import { motion } from "framer-motion";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";
import { FALLBACK_PACKAGES } from "@/lib/fallbackPackages";

const SITE = "https://www.monseraphinstudio.fr";

// Rich SEO content per formula slug
const FORMULA_SEO = {
    decouverte: {
        slug: "decouverte",
        api_id: "decouverte",
        title: "Formule Découverte 90€ — 1h de studio podcast pro · Mon Séraphin",
        description: "Testez le studio podcast Mon Séraphin sans engagement. 1h d'enregistrement audio professionnel, setup micros & casques, technicien sur place. À 90€ TTC. Villiers-sur-Marne · 13 min du RER A.",
        keywords: "studio podcast 1h, location studio podcast pas cher, essai studio podcast, enregistrement podcast Villiers-sur-Marne, studio podcast Île-de-France, podcast débutant, formule découverte podcast, studio podcast 90 euros, studio podcast Paris Est, studio podcast RER A, studio podcast RER E",
        intro: "Vous lancez un podcast ? Vous voulez tester la qualité d'un vrai studio sans vous engager ? La formule Découverte est faite pour vous : 1h dans nos murs, micros broadcast, casques, technicien dédié, et vous repartez avec vos fichiers bruts en HD prêts à être édités.",
        why: [
            "1h dans un studio pro, idéal pour un premier épisode test",
            "Aucun frais caché : matériel, technicien et fichiers HD inclus",
            "Atmosphère cinématique et acoustique maîtrisée",
            "Idéal pour valider votre format avant un engagement plus long",
        ],
        forWho: "Créateurs débutants, entrepreneurs qui hésitent à se lancer, podcasteurs qui veulent tester un nouveau format, marques qui veulent essayer le branded podcast.",
        process: [
            { step: "Réservez en 2 min", text: "Choisissez votre date et créneau directement en ligne." },
            { step: "Préparez votre épisode", text: "On vous envoie un mémo technique pour optimiser votre temps." },
            { step: "Arrivez et enregistrez", text: "Le technicien s'occupe de tout le setup. Vous parlez, c'est tout." },
            { step: "Repartez avec vos fichiers", text: "Téléchargement des pistes brutes en haute définition sous 24h." },
        ],
        faq: [
            { q: "Que contient exactement la formule Découverte ?", a: "1h de studio (micros Shure SM7B, casques broadcast), un technicien dédié, et la livraison de vos fichiers audio bruts en haute définition sous 24h." },
            { q: "Le montage est-il inclus ?", a: "Non, le montage n'est pas inclus dans la Découverte. Vous pouvez l'ajouter en option à partir de 75€ au moment de la réservation." },
            { q: "Puis-je inviter un guest ?", a: "Oui, jusqu'à 2 personnes incluses (vous + 1 invité). Pour 3 ou 4 intervenants, optez pour la formule Premium." },
        ],
    },
    abonnement: {
        slug: "abonnement",
        api_id: "abonnement",
        title: "Formule Abonnement 350€/mois — 3h studio/sem + contenu mensuel · Mon Séraphin",
        description: "Production récurrente clé-en-main : 3h de studio par semaine, pack contenu mensuel (shorts, posts, miniatures) et accompagnement éditorial. 350€/mois. Pour podcasteurs sérieux qui veulent durer.",
        keywords: "abonnement studio podcast, podcast récurrent, podcast hebdomadaire, agence podcast mensuelle, accompagnement podcast, podcast B2B, podcast entreprise, podcast clé en main mensuel, studio podcast abonnement Paris, studio podcast Paris Est, studio podcast RER A, studio podcast RER E, podcast Île-de-France",
        intro: "Vous publiez chaque semaine ? L'abonnement Mon Séraphin vous donne 3h de studio par semaine, un pack contenu mensuel (shorts, visuels, posts), et un accompagnement éditorial pour rester sur la bonne ligne. Stop à la procrastination, place à la régularité.",
        why: [
            "3h de studio par semaine = 4 sessions par mois",
            "Pack contenu mensuel inclus (shorts, miniatures, visuels social)",
            "Accompagnement éditorial : conseils format, invités, séquençage",
            "Priorité sur les créneaux populaires",
            "Tarif optimisé vs réservation à l'unité",
        ],
        forWho: "Créateurs de contenu, commerçants et entrepreneurs qui veulent publier chaque semaine, podcasteurs hebdomadaires, marques B2B, agences, formateurs qui produisent du contenu en continu.",
        process: [
            { step: "Onboarding stratégique", text: "Premier rendez-vous pour caler ligne éditoriale, calendrier et invités." },
            { step: "Studio chaque semaine", text: "Vos 3h hebdo dans nos murs, planifiées à l'avance." },
            { step: "Production en continu", text: "Montage + pack contenu mensuel livré au fil de l'eau." },
            { step: "Reporting mensuel", text: "Bilan production + pistes d'amélioration." },
        ],
        faq: [
            { q: "L'engagement est-il long ?", a: "Engagement minimum de 3 mois pour assurer la régularité. Au-delà, sans engagement avec préavis d'1 mois." },
            { q: "Que contient le pack contenu mensuel ?", a: "8 à 12 shorts verticaux, 4 miniatures YouTube, 4 visuels social media et 1 série de 4 posts LinkedIn par mois." },
            { q: "Puis-je faire venir des invités ?", a: "Oui, autant que vous voulez. Le studio peut accueillir jusqu'à 4 intervenants." },
            { q: "Que se passe-t-il si je n'utilise pas mes 3h une semaine ?", a: "Les heures non utilisées sont reportables sur le mois en cours, dans la limite de 6h reportées." },
        ],
    },
    premium: {
        slug: "premium",
        api_id: "premium",
        title: "Formule Premium 620€ — 1/2 journée studio + stratégie + pack complet · Mon Séraphin",
        description: "L'expérience podcast haut de gamme : 1/2 journée en studio, session stratégie personnalisée, pack contenu complet (épisode, shorts, visuels, miniatures premium). 620€ TTC. Pour ceux qui veulent un impact maximal.",
        keywords: "podcast premium, podcast haut de gamme, agence podcast luxe, branded podcast entreprise, podcast direction marketing, studio podcast clé en main premium, podcast stratégique, agence podcast Paris premium, podcast B2B haut de gamme, studio podcast Paris Est, studio podcast RER A, studio podcast RER E, podcast Île-de-France",
        intro: "Pour les marques et personnalités qui veulent un podcast à fort impact, la formule Premium combine demi-journée en studio, session stratégie personnalisée avec un consultant éditorial, et un pack contenu complet : épisode, shorts, miniatures premium, visuels. Tout est pensé pour maximiser votre portée.",
        why: [
            "1/2 journée complète (4h) en studio, sans pression de timing",
            "Session stratégie personnalisée avec un consultant éditorial",
            "Pack contenu complet : épisode + 5 shorts + miniatures YouTube premium + 8 visuels social",
            "Accompagnement prioritaire et créneaux flexibles",
            "Multi-caméras vidéo possible en option",
        ],
        forWho: "Dirigeants, marques premium, personnalités publiques, entreprises qui lancent un podcast B2B avec ambition, agences qui veulent offrir un service haut de gamme à leurs clients.",
        process: [
            { step: "Brief stratégique 1h", text: "Avant le studio, on cale positionnement, angles et arc narratif." },
            { step: "1/2 journée en studio", text: "Production sereine, multiples prises possibles, pauses incluses." },
            { step: "Production complète", text: "Montage premium + pack contenu visuel + miniatures CTR-optimisées." },
            { step: "Livraison et conseils diffusion", text: "Recommandations de publication et de promotion sur chaque plateforme." },
        ],
        faq: [
            { q: "La vidéo est-elle incluse ?", a: "L'épisode peut être enregistré en vidéo (jusqu'à 4 caméras en option). Les 5 shorts inclus sont vidéo. Un montage vidéo long-format peut être ajouté." },
            { q: "Combien de personnes peut-on inviter ?", a: "Jusqu'à 4 invités simultanément. Pour des plateaux à plus de 4, contactez-nous." },
            { q: "L'accompagnement stratégique est-il vraiment inclus ?", a: "Oui, 1h de stratégie avec un consultant éditorial avant la session studio, pour calibrer le format et maximiser l'impact." },
            { q: "Quel délai de livraison ?", a: "5 jours ouvrés pour la livraison complète (épisode + tout le pack contenu)." },
        ],
    },
};

export default function FormuleDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const data = FORMULA_SEO[slug];
    const [pkg, setPkg] = useState(null);

    useEffect(() => {
        if (!data) {
            navigate("/", { replace: true });
            return;
        }
        // Initialize with fallback so the page renders even if API is down
        const fbk = FALLBACK_PACKAGES.find((p) => p.id === data.api_id);
        if (fbk) setPkg(fbk);
        api.get("/packages")
            .then((r) => {
                const found = (r.data?.packages || []).find((p) => p.id === data.api_id);
                if (found) setPkg(found);
            })
            .catch(() => {
                // keep fallback
            });
        window.scrollTo(0, 0);
    }, [slug, data, navigate]);

    if (!data) return null;

    const structured = pkg && {
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: "Studio podcast",
        provider: {
            "@type": "LocalBusiness",
            name: "Studio Mon Séraphin",
            "@id": `${SITE}/#business`,
        },
        name: pkg.name,
        description: data.description,
        url: `${SITE}/formules/${slug}`,
        offers: {
            "@type": "Offer",
            price: pkg.price,
            priceCurrency: "EUR",
            availability: "https://schema.org/InStock",
            url: `${SITE}/reserver?package=${pkg.id}`,
        },
        areaServed: { "@type": "AdministrativeArea", name: "Île-de-France" },
    };

    return (
        <>
            <SEO
                title={data.title}
                description={data.description}
                path={`/formules/${slug}`}
                keywords={data.keywords}
                structuredData={structured}
            />
            <Navbar />
            <main className="min-h-screen pt-32 pb-20 px-6 md:px-12" data-testid={`formule-page-${slug}`}>
                <div className="max-w-5xl mx-auto">
                    <Link to="/#formules" className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-amber-300 mb-8">
                        <ChevronLeft className="w-4 h-4" /> Toutes les formules
                    </Link>

                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                        <span className="font-body text-xs uppercase tracking-[0.25em] text-zinc-500">Formule</span>
                        <h1 className="mt-3 font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-white leading-[1.05]">
                            {pkg?.name || data.slug}
                        </h1>
                        <p className="mt-4 text-lg text-zinc-400 font-body max-w-2xl leading-relaxed">{data.intro}</p>

                        {pkg && (
                            <div className="mt-8 flex items-baseline gap-3 flex-wrap">
                                <span className="font-display text-5xl text-white font-light tracking-tight">{pkg.price}€</span>
                                <span className="text-sm text-zinc-500">/ {pkg.unit}</span>
                                <span className="text-zinc-600">·</span>
                                <span className="text-sm text-zinc-400 inline-flex items-center gap-1.5"><Clock className="w-4 h-4 text-amber-300" /> {pkg.duration}</span>
                            </div>
                        )}
                        {pkg && (
                            <p className="mt-4 inline-flex items-center gap-2 rounded-md border border-amber-300/30 bg-amber-300/[0.06] px-3 py-1.5 text-sm text-amber-200 font-body" data-testid="formule-installments">
                                Paiement en 3 fois sans frais : 3 × {(pkg.price / 3).toLocaleString("fr-FR", { maximumFractionDigits: 2 })}€
                            </p>
                        )}

                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link
                                to={`/reserver?package=${data.api_id}`}
                                className="inline-flex items-center gap-2 rounded-md bg-amber-300 px-6 py-3 text-sm font-medium text-zinc-950 hover:bg-amber-200 transition"
                                data-testid={`reserve-formule-${slug}-btn`}
                            >
                                Réserver cette formule
                                <ArrowRight className="w-4 h-4" />
                            </Link>
                            <a href="tel:+33758818179" className="inline-flex items-center gap-2 rounded-md border border-white/15 px-6 py-3 text-sm text-white hover:border-white/40 hover:bg-white/5">
                                Nous appeler
                            </a>
                        </div>
                    </motion.div>

                    {/* Pourquoi cette formule */}
                    <section className="mt-20">
                        <h2 className="font-display text-2xl md:text-3xl font-light tracking-tight text-white">
                            Pourquoi choisir la formule {pkg?.name || data.slug} ?
                        </h2>
                        <ul className="mt-8 grid sm:grid-cols-2 gap-4">
                            {data.why.map((b, i) => (
                                <li key={i} className="flex items-start gap-3 p-5 rounded-xl border border-white/8 bg-white/[0.02]">
                                    <Sparkles className="w-4 h-4 text-amber-300 mt-1 shrink-0" />
                                    <span className="text-zinc-300">{b}</span>
                                </li>
                            ))}
                        </ul>
                    </section>

                    {/* Pour qui */}
                    <section className="mt-16">
                        <h2 className="font-display text-2xl md:text-3xl font-light tracking-tight text-white">Pour qui ?</h2>
                        <p className="mt-4 text-zinc-300 leading-relaxed max-w-3xl">{data.forWho}</p>
                    </section>

                    {/* Inclus */}
                    {pkg?.features && (
                        <section className="mt-16">
                            <h2 className="font-display text-2xl md:text-3xl font-light tracking-tight text-white">Ce qui est inclus</h2>
                            <ul className="mt-6 space-y-3">
                                {pkg.features.map((f) => (
                                    <li key={f} className="flex items-start gap-3 text-zinc-300">
                                        <Check className="w-4 h-4 text-amber-300 mt-1 shrink-0" /> {f}
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    {/* Process */}
                    <section className="mt-16">
                        <h2 className="font-display text-2xl md:text-3xl font-light tracking-tight text-white">Comment ça se passe ?</h2>
                        <ol className="mt-8 space-y-4">
                            {data.process.map((s, i) => (
                                <li key={i} className="flex gap-5 p-5 rounded-xl border border-white/8 bg-white/[0.02]">
                                    <span className="font-display text-2xl text-amber-300 font-light w-8 shrink-0">0{i + 1}</span>
                                    <div>
                                        <p className="font-display text-base text-white font-medium">{s.step}</p>
                                        <p className="text-sm text-zinc-400 mt-1">{s.text}</p>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </section>

                    {/* FAQ */}
                    <section className="mt-16">
                        <h2 className="font-display text-2xl md:text-3xl font-light tracking-tight text-white">Questions fréquentes</h2>
                        <div className="mt-6 space-y-3">
                            {data.faq.map((f, i) => (
                                <details key={i} className="rounded-xl border border-white/8 bg-white/[0.02] p-5 group">
                                    <summary className="font-display text-base text-white font-medium cursor-pointer list-none flex items-center justify-between">
                                        {f.q}
                                        <span className="text-amber-300 group-open:rotate-45 transition-transform">+</span>
                                    </summary>
                                    <p className="mt-3 text-zinc-400 leading-relaxed">{f.a}</p>
                                </details>
                            ))}
                        </div>
                    </section>

                    {/* Localisation */}
                    <section className="mt-16 p-6 rounded-xl border border-white/8 bg-white/[0.02]">
                        <p className="text-sm text-zinc-400 inline-flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-amber-300" />
                            Studio Mon Séraphin · 5 Boulevard Georges Méliès, Villiers-sur-Marne · à 13 min à pied du RER A Noisy-le-Grand
                        </p>
                    </section>

                    {/* CTA final */}
                    <section className="mt-12 rounded-xl border border-amber-300/30 bg-amber-300/[0.05] p-8 md:p-10">
                        <h3 className="font-display text-2xl md:text-3xl font-light text-white">
                            Prêt·e à réserver votre {pkg?.name || data.slug} ?
                        </h3>
                        <p className="mt-2 text-zinc-400 max-w-xl">Réservation en ligne en moins de 2 minutes. Paiement en 3 fois sans frais avec Scalapay ou règlement sur place.</p>
                        <Link
                            to={`/reserver?package=${data.api_id}`}
                            className="mt-6 inline-flex items-center gap-2 rounded-md bg-amber-300 px-6 py-3 text-sm font-medium text-zinc-950 hover:bg-amber-200 transition"
                        >
                            Réserver maintenant <ArrowRight className="w-4 h-4" />
                        </Link>
                    </section>
                </div>
            </main>
            <Footer />
        </>
    );
}
